/**
 * Reading somebody's own template and putting it on the avatar.
 *
 * The file **never leaves the browser**: it is decoded, drawn to the canvas the
 * viewer already samples, and forgotten when the tab closes. That is worth
 * saying on the page — it is true, it is unusual, and it is the same promise
 * the privacy page makes.
 *
 * The decision half is pure and lives here so the wording of a refusal is
 * testable without a file picker. Phase 1 scaffolds the path; the fuller error
 * handling is phase 2.
 */
import { CANVAS } from "../../shared/format";

/** Refuse before decoding. A 40 MB PNG should be declined, not parsed. */
export const MAX_BYTES = 12 * 1024 * 1024;

export type TemplateIssue =
  | { kind: "ok" }
  | { kind: "too-big"; message: string }
  | { kind: "not-an-image"; message: string }
  | { kind: "wrong-size"; message: string; width: number; height: number };

/**
 * Is this file worth decoding at all?
 *
 * Type first, then size — a 40 MB video refused for being large would be
 * refused for the wrong reason, and the person would try a smaller video.
 */
export function checkFile(file: { type: string; size: number }): TemplateIssue {
  if (file.type && !file.type.startsWith("image/")) {
    return {
      kind: "not-an-image",
      message: "That isn't an image. Roblox templates are PNG files.",
    };
  }
  if (file.size > MAX_BYTES) {
    return {
      kind: "too-big",
      message: `That file is ${Math.round(file.size / 1_000_000)} MB. Templates are usually well under 1 MB.`,
    };
  }
  return { kind: "ok" };
}

/**
 * Are these the dimensions Roblox accepts?
 *
 * Names both sizes, because "wrong size" alone leaves somebody guessing at
 * which of the two numbers to change. Wrong is not fatal — the caller still
 * offers to show it — so this returns a message, not a rejection.
 */
export function checkSize(width: number, height: number): TemplateIssue {
  if (width === CANVAS.width && height === CANVAS.height) return { kind: "ok" };
  return {
    kind: "wrong-size",
    width,
    height,
    message: `That image is ${width}×${height}. Roblox classic templates are ${CANVAS.width}×${CANVAS.height}.`,
  };
}

/**
 * Draw a decoded image into the template canvas the viewer samples.
 *
 * Always cleared first: a smaller image dropped over a larger one would
 * otherwise leave the previous artwork showing around its edges, which reads
 * as the new file being broken.
 *
 * A wrong-sized image is scaled to fit rather than refused, so somebody testing
 * with whatever PNG is nearest still sees the thing work.
 */
export function drawTemplate(
  target: HTMLCanvasElement,
  source: CanvasImageSource,
  size: { width: number; height: number },
): void {
  const ctx = target.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, target.width, target.height);
  if (size.width === target.width && size.height === target.height) {
    ctx.drawImage(source, 0, 0);
    return;
  }
  const scale = Math.min(target.width / size.width, target.height / size.height);
  const w = size.width * scale;
  const h = size.height * scale;
  ctx.drawImage(source, (target.width - w) / 2, (target.height - h) / 2, w, h);
}
